import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import ApiIcon from '@mui/icons-material/Api';
import AppleIcon from '@mui/icons-material/Apple';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WindowIcon from '@mui/icons-material/Window';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grow,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import tokenService from '@sophocles/services/token';
import ButtonBox from '@sophocles/views/auth/forms/ButtonBox';

function getIdpIcon(idpName) {
  switch (idpName) {
    case 'Google':
      return <GoogleIcon />;
    case 'Facebook':
      return <FacebookIcon />;
    case 'Microsoft365':
      return <WindowIcon />;
    case 'Apple':
      return <AppleIcon />;
    default:
      return <ApiIcon />;
  }
}

function openIdpUrl(idpUrl) {
  const platform = Capacitor.getPlatform();

  if (platform === 'ios' || platform === 'android') {
    // Decode the URL to make it readable
    const decodedUrl = decodeURIComponent(idpUrl);

    // Replace the web URL with the custom URL scheme
    const newUrl = decodedUrl.replace('https://portal.outdoorlinkinc.com/', 'odl://');

    // Re-encode the entire URL
    //const encodedUrl = encodeURIComponent(newUrl);

    Browser.open({ url: newUrl });
  } else {
    window.location.href = idpUrl;
  }
}

export default function LoginForm({
  error,
  isVerified,
  isSubmitting,
  isEnabled,
  identityProviders,
  onVerify,
  onLogin,
  onCancel,
  onResetPassword,
}) {
  const isNativePlatform = Capacitor.isNativePlatform();
  const { t } = useTranslation();

  const isMediumScreen = useMediaQuery(theme => theme.breakpoints.down('md'));

  const [username, setUsername] = useState(
    tokenService.rememberMe ? (tokenService.user ? tokenService.user.email : '') : '',
  );
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(tokenService.rememberMe ?? false);

  const handleChange = event => {
    if (event.target.name === 'username') {
      setUsername(event.target.value.trim());
      if (isVerified) {
        setPassword('');
      }
    } else if (event.target.name === 'password') {
      setPassword(event.target.value);
    }
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (isVerified) {
      onLogin(username.toLowerCase(), password);
    } else {
      onVerify(username.toLowerCase());
    }
  };

  const handleCancel = event => {
    event.preventDefault();
    onCancel();
  };

  const isSSOUser = username.endsWith('@lamar.com');

  return (
    <form id="login-form" style={{ display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit}>
      <TextField
        id="login-form-username"
        autoFocus
        size={isMediumScreen ? 'medium' : 'small'}
        placeholder={t('general.emailAddress')}
        label={t('general.emailAddress')}
        name="username"
        tabIndex={0}
        value={username}
        autoComplete="email"
        inputMode="email"
        onChange={handleChange}
        disabled={isSubmitting || isVerified}
        error={error && !isVerified}
        sx={{
          mb: 2,
        }}
      />
      {isVerified && !isSSOUser ? (
        <Grow in={isVerified}>
          <TextField
            id="login-form-password"
            size={isMediumScreen ? 'medium' : 'small'}
            inputRef={input => input && input.focus()}
            type={showPassword ? 'text' : 'password'}
            tabIndex={2}
            placeholder={t('auth.password')}
            label={t('auth.password')}
            name="password"
            autoComplete="current-password"
            value={password}
            error={error}
            onChange={handleChange}
            disabled={isSubmitting}
            sx={{
              mb: 2,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grow>
      ) : null}
      {isVerified && !isSSOUser && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FormControlLabel
            label={t('auth.rememberMe')}
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e, newValue) => {
                  setRememberMe(newValue);
                  tokenService.rememberMe = newValue;
                }}
              />
            }
          />
        </Box>
      )}
      <ButtonBox sx={{ my: 2, px: 4 }}>
        {(!isSSOUser || !isVerified) && (
          <LoadingButton
            id="submit-login"
            name="login"
            tabIndex={isVerified ? 2 : 1}
            variant="contained"
            color="primary"
            type="submit"
            size={isMediumScreen ? 'large' : 'medium'}
            disabled={isSubmitting || !isEnabled}
            loading={isSubmitting}
            fullWidth
          >
            {t('auth.signIn')}
          </LoadingButton>
        )}
        {isVerified ? (
          <Button
            fullWidth
            id="cancel-login"
            name="cancel"
            tabIndex={isVerified ? 3 : 2}
            variant="contained"
            color="error"
            type="cancel"
            size={isMediumScreen ? 'large' : 'medium'}
            disabled={isSubmitting}
            onClick={handleCancel}
          >
            {t('actions.cancel')}
          </Button>
        ) : null}
      </ButtonBox>
      {isVerified && !isSSOUser ? (
        <Box style={{ padding: '32px' }}>
          <Button
            fullWidth
            variant="text"
            size={isMediumScreen ? 'large' : 'medium'}
            onClick={() => onResetPassword(false, false)}
            sx={{ mb: 2 }}
            disabled={isSubmitting || !isEnabled}
          >
            {t('auth.forgotPassword')}
          </Button>
        </Box>
      ) : null}
      {isVerified && identityProviders.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          <Divider sx={{ mb: 4 }}>
            <Typography variant="caption">{t('auth.orSignInWith')}</Typography>
          </Divider>
          <Box sx={{ px: 4 }}>
            {identityProviders.map(idp => (
              <Grow key={idp} in={true}>
                <Button
                  variant="contained"
                  size={isMediumScreen ? 'large' : 'medium'}
                  disabled={isSubmitting || !isEnabled}
                  fullWidth
                  sx={{ mb: 2 }}
                  onClick={() => openIdpUrl(idp.url)}
                  startIcon={getIdpIcon(idp.name)}
                >
                  {idp.name}
                </Button>
              </Grow>
            ))}
          </Box>
        </Box>
      ) : null}
    </form>
  );
}
